import { useState } from "react";

import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import type { SxProps } from "@mui/material/styles";

import PriorityLabel from "../../components/priority-label.component";

type GroupByPriorityContainerProps = {
  priority: number;
  count: number;
  showNoDataPlaceHolder?: boolean;
  collapsedSx?: SxProps;
  expandedSx?: SxProps;
  hasMore?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  children?: React.ReactNode;
};

export default function GroupByPriorityContainer({
  priority,
  count,
  showNoDataPlaceHolder = true,
  collapsedSx,
  expandedSx,
  hasMore,
  onLoadMore,
  isLoadingMore,
  children
}: GroupByPriorityContainerProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <Stack mb={3} sx={isExpanded ? expandedSx : collapsedSx}>
      <Stack
        direction="row"
        alignItems="center"
        ml={0}
        sx={{ cursor: "pointer", width: "fit-content" }}
        onClick={() => {
          setIsExpanded((prev) => !prev);
        }}
      >
        <IconButton>
          {isExpanded ? (
            <ExpandLessIcon sx={{ transform: "scale(1.5)" }} />
          ) : (
            <ExpandMoreIcon sx={{ transform: "scale(1.5)" }} />
          )}
        </IconButton>
        <Box>
          <PriorityLabel
            priority={priority}
            count={count}
            sx={{ fontSize: "18px", letterSpacing: 2 }}
          />
        </Box>
      </Stack>
      {count === 0 && showNoDataPlaceHolder && isExpanded ? (
        <Typography variant="body1" sx={{ pl: "24px", color: grey[700] }}>
          No tasks found
        </Typography>
      ) : (
        <Box>
          {isExpanded ? children : undefined}
          {isExpanded && hasMore && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onLoadMore?.();
                }}
                disabled={isLoadingMore}
                sx={{ textTransform: "none" }}
              >
                {isLoadingMore ? (
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                ) : null}
                Load more
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Stack>
  );
}
